import { useRef } from 'react'
import * as Form from '@/features/form'
import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'
import { DefaultValues } from 'react-hook-form'

import {
  FormData,
  TRANSLATABLE_FIELDS,
  BUTTON_TRANSLATABLE_FIELDS,
  ALL_TRANSLATABLE_FIELDS
} from '../hooks/useUploadButtonRow'

import { graphqlToForm } from '@/features/translations'
import { useBranding } from '@/features/branding'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { ButtonRowItems } from './ButtonRowItems'
import { ButtonRow, isDirtyButtonRow, isNewButtonRow } from '../types'
import { useShowDoorSelector } from '@/features/integrationDoor'

type Props = {
  value: ButtonRow | undefined | null
  onDone: (value: FormData) => void
  onCancel: () => void
}

function createDefaultValues(
  value: ButtonRow,
  supportedLocales: GraphQL.AppLocale[] | null | undefined
) {
  if (isNewButtonRow(value)) return value
  if (isDirtyButtonRow(value)) return value

  return {
    ...value,
    translations: graphqlToForm(
      getFragment(
        SharedAPI.translation.TRANSLATION_FRAGMENT,
        value.translations
      ) ?? [],
      TRANSLATABLE_FIELDS,
      supportedLocales ?? []
    ),
    items: value.items.map(function (item, index) {
      const itemFrag = getFragment(SharedAPI.buttonRow.ITEM_FRAGMENT, item)

      return {
        ...itemFrag,
        translations: graphqlToForm(
          getFragment(
            SharedAPI.translation.TRANSLATION_FRAGMENT,
            itemFrag.translations
          ) ?? [],
          BUTTON_TRANSLATABLE_FIELDS,
          supportedLocales ?? [],
          {
            prefix: `items.${index}.`
          }
        )
      }
    })
  }
}

export function ButtonRowForm({ value, onDone, onCancel }: Props) {
  const { t } = useLingui()
  const showDoorSelector = useShowDoorSelector()

  const {
    branding: { supportedLocales }
  } = useBranding()

  const defaultValues = useRef<DefaultValues<FormData>>(
    value ? createDefaultValues(value, supportedLocales) : { items: [] }
  )

  return (
    <Form.Root<FormData>
      defaultValues={defaultValues.current}
      onSubmit={onDone}
    >
      <div className="flex flex-col gap-5 p-2.5">
        <Form.Translation translatableFields={ALL_TRANSLATABLE_FIELDS}>
          <Form.Field
            name="header"
            label={t`Header`}
            rules={{
              maxLength: {
                value: 255,
                message: t`Can't be longer than 255 characters.`
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                onChange={event => onChange(event.target.value)}
                type="text"
                {...props}
              />
            )}
          </Form.Field>
          <ButtonRowItems name="items" showDoorSelector={showDoorSelector} />
          <footer className="flex flex-row items-center justify-between gap-4">
            <Button variant="outline" onClick={onCancel} type="button">
              <Trans>Cancel</Trans>
            </Button>
            <Form.Submit>
              <Trans>Save</Trans>
            </Form.Submit>
          </footer>
        </Form.Translation>
      </div>
    </Form.Root>
  )
}
